/**
 * RoleViewPage - Student role assignment and game interface
 * Shows assigned role, dossier, and game progress with tabbed navigation
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { SessionStatus, SessionStatusResponse } from '@/types/session';
import { sessionsService } from '@/services/sessions';
import { GameView } from '@/game/components/GameView';
import { GameState, StageId, StageVariant, STAGES } from '@/game/models/GameState';
import { PlayerState, PlayerRole } from '@/game/models/Player';
import { InventoryItem, ItemId } from '@/game/models/Inventory';
import { AnswerValue } from '@/game/models/Question';
import { getPlayerIcon } from '@/config/playerIcons';
import { EmailCollectionForm } from '@/components/game/EmailCollectionForm';
import { EnhancedResultsScreen } from '@/components/game/EnhancedResultsScreen';

// New UI components
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Header } from '@/components/game/Header';
import { LoadingState, ErrorState, PlayerNotFoundState } from '@/components/game/States';
import { MissionDocuments } from '@/components/game/MissionDocuments';
import { TeamRoster } from '@/components/game/TeamRoster';
import { getRoleConfig } from '@/components/game/RoleCard';
import { InventoryPage } from '@/game/components/InventoryPage';
import { GameNotification } from '@/components/game/GameNotification';
import { MessagePanel } from '@/components/game/MessagePanel';
import { MessageNotification } from '@/components/game/MessageNotification';
import { cn } from '@/lib/utils';
import { Gamepad2, Users, FileText, Bell, Package } from 'lucide-react';
import { usePlayerMessages } from '@/hooks/usePlayerMessages';

// Helper to convert API game state to frontend GameState model
function convertToGameState(
  sessionId: string,
  apiGameState: NonNullable<SessionStatusResponse['gameState']>
): GameState {
  // Convert inventory items with proper type casting
  const inventory: InventoryItem[] = apiGameState.inventory.map(item => ({
    id: item.id as ItemId,
    name: item.name,
    quantity: item.quantity,
  }));

  return {
    sessionId,
    createdAt: new Date(),
    updatedAt: new Date(),
    currentStage: apiGameState.currentStage as StageId,
    currentStep: apiGameState.currentStep,
    currentVariant: apiGameState.currentVariant as StageVariant,
    huntingPermitDay: apiGameState.huntingPermitDay,
    players: apiGameState.players.map(p => ({
      playerId: p.playerId,
      sessionId,
      role: p.role as PlayerRole,
      name: p.name,
      scores: { brain: 0, heart: 0, task: 0, communication: 0 },
      energy: p.energy,
      isAlive: p.isAlive,
      isPoisoned: p.isPoisoned,
      swimming: false,
      decisions: new Map(
        Object.entries(p.submissionStatus || {}).map(([qId, val]) => [qId, val])
      ),
    })),
    npcs: apiGameState.npcs.map(n => ({
      role: n.role,
      energy: n.energy,
      decisions: new Map(Object.entries(n.decisions)),
    })),
    teamName: apiGameState.teamName,
    teamEnergyPacks: apiGameState.teamEnergyPacks,
    inventory,
    decisions: [],
    flags: apiGameState.flags,
    gameStatus: apiGameState.gameStatus,
  };
}

// Helper to convert API player state to frontend PlayerState model
function convertToPlayerState(
  sessionId: string,
  apiPlayerState: NonNullable<SessionStatusResponse['currentPlayerState']>
): PlayerState {
  return {
    playerId: apiPlayerState.playerId,
    sessionId,
    role: apiPlayerState.role as PlayerRole,
    name: apiPlayerState.name,
    scores: apiPlayerState.scores,
    energy: apiPlayerState.energy,
    isAlive: apiPlayerState.isAlive,
    isPoisoned: apiPlayerState.isPoisoned,
    swimming: apiPlayerState.swimming,
    decisions: new Map(Object.entries(apiPlayerState.decisions)),
  };
}

export function RoleViewPage() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [session, setSession] = useState<SessionStatusResponse | null>(null);
  const [currentPlayer, setCurrentPlayer] = useState<SessionStatusResponse['players'][0] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [playerId, setPlayerId] = useState<string | null>(null);
  const [gameState, setGameState] = useState<GameState | null>(null);
  const [playerState, setPlayerState] = useState<PlayerState | null>(null);
  const [answeredQuestions, setAnsweredQuestions] = useState<Set<string>>(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [currentStage, setCurrentStage] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState('game');
  const [emailSubmitted, setEmailSubmitted] = useState(false);
  const lastPlayerStateRef = useRef<PlayerState | null>(null);

  // Notification state for energy/inventory changes
  const [notification, setNotification] = useState<{
    type: 'energy' | 'inventory';
    message: string;
    change?: number;
    itemName?: string;
  } | null>(null);

  // Message panel and notification state
  const [isMessagePanelOpen, setIsMessagePanelOpen] = useState(false);
  const [messageNotification, setMessageNotification] = useState<{
    count: number;
    stageName: string;
  } | null>(null);
  const prevStageForMessagesRef = useRef<number | null>(null);

  // Track previous values to detect changes
  const prevEnergyRef = useRef<number | null>(null);
  const prevInventoryRef = useRef<InventoryItem[]>([]);

  // Get player ID - prefer URL param (test mode) over sessionStorage
  const testPlayerId = searchParams.get('testPlayerId');
  const isTestModeEnabled = localStorage.getItem('cesim_test_mode') === 'true';
  const effectivePlayerId = (testPlayerId && isTestModeEnabled) ? testPlayerId : null;

  useEffect(() => {
    if (effectivePlayerId) {
      // Test mode: use player ID from URL (don't store in sessionStorage to avoid conflicts between tabs)
      setPlayerId(effectivePlayerId);
    } else {
      // Normal mode: get player ID from sessionStorage
      const storedPlayerId = sessionStorage.getItem('studentPlayerId');
      if (!storedPlayerId) {
        // Player didn't join properly, redirect to join page
        navigate('/join');
        return;
      }
      setPlayerId(storedPlayerId);
    }

    // Load initial session data
    loadSession();

    // Poll for session updates every 5 seconds
    const pollInterval = setInterval(loadSession, 5000);

    return () => clearInterval(pollInterval);
  }, [sessionId, navigate, effectivePlayerId]);

  async function loadSession() {
    if (!sessionId) {
      setError('Session ID is missing');
      setLoading(false);
      return;
    }

    // Use test player ID from URL if in test mode, otherwise use sessionStorage
    const currentPlayerId = effectivePlayerId || sessionStorage.getItem('studentPlayerId');
    if (!currentPlayerId) {
      setError('Player ID is missing');
      setLoading(false);
      return;
    }

    try {
      // Use student-specific unauthenticated endpoint
      const data = await sessionsService.getSessionStatus(sessionId, currentPlayerId);
      setSession(data);

      // Find current player
      const player = data.players.find((p) => p.playerId === currentPlayerId);
      setCurrentPlayer(player || null);

      // If session is still waiting, redirect to waiting room
      if (data.status === SessionStatus.WAITING) {
        navigate(`/student/waiting/${sessionId}`);
        return;
      }

      // If session is complete, show completion message
      if (data.status === SessionStatus.COMPLETE) {
        // Keep showing role view but update UI to indicate completion
      }

      // If session is LIVE and has game state, set it up
      if (data.status === SessionStatus.LIVE && data.gameState && data.currentPlayerState) {
        const convertedGameState = convertToGameState(sessionId, data.gameState);
        const convertedPlayerState = convertToPlayerState(sessionId, data.currentPlayerState);

        // Clear answered questions when stage changes
        if (currentStage !== null && currentStage !== data.gameState.currentStage) {
          setAnsweredQuestions(new Set());
        }
        setCurrentStage(data.gameState.currentStage);

        setGameState(convertedGameState);
        setPlayerState(convertedPlayerState);
      }

      setError(null);
    } catch (err: unknown) {
      console.error('Failed to load session:', err);
      const apiMessage =
        typeof err === 'object' &&
        err !== null &&
        'response' in err &&
        typeof (err as { response?: unknown }).response === 'object' &&
        (err as { response?: { data?: unknown } }).response?.data &&
        typeof (err as { response?: { data?: { message?: unknown } } }).response?.data?.message === 'string'
          ? (err as { response: { data: { message: string } } }).response.data.message
          : null;
      setError(
        apiMessage ||
          'Failed to load session. The session may have been deleted.'
      );
    } finally {
      setLoading(false);
    }
  }

  // Handle answer submission
  const handleSubmitAnswer = useCallback(async (questionId: string, answer: AnswerValue) => {
    if (!sessionId || !playerId || submitting) return;

    try {
      setSubmitting(true);

      // Submit decision to backend
      const result = await sessionsService.submitDecision(
        sessionId,
        playerId,
        questionId,
        answer
      );

      // Mark question as answered (hide it from view)
      setAnsweredQuestions(prev => new Set(prev).add(questionId));

      console.log('Decision submitted:', {
        questionId,
        answer,
        allAnswered: result.allAnswered,
      });

      // Refresh session data to get updated game state
      await loadSession();
    } catch (err: unknown) {
      console.error('Failed to submit answer:', err);
      // Show error to user
      const apiMessage =
        typeof err === 'object' &&
        err !== null &&
        'response' in err &&
        typeof (err as { response?: unknown }).response === 'object' &&
        (err as { response?: { data?: unknown } }).response?.data &&
        typeof (err as { response?: { data?: { message?: unknown } } }).response?.data?.message === 'string'
          ? (err as { response: { data: { message: string } } }).response.data.message
          : null;
      setError(apiMessage || 'Failed to submit answer. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }, [sessionId, playerId, submitting]);

  // Detect energy and inventory changes for notifications
  useEffect(() => {
    if (!playerState) return;

    // Detect energy change
    if (prevEnergyRef.current !== null && prevEnergyRef.current !== playerState.energy) {
      const change = playerState.energy - prevEnergyRef.current;
      setNotification({
        type: 'energy',
        message: change > 0 ? 'Energy restored!' : 'Energy depleted',
        change,
      });
    }
    prevEnergyRef.current = playerState.energy;

    // Detect inventory change
    if (gameState?.inventory) {
      const prevItems = prevInventoryRef.current;
      const newItems = gameState.inventory.filter(
        item => !prevItems.some(p => p.id === item.id)
      );
      // Also detect quantity increases for existing items
      const increasedItems = gameState.inventory.filter(item => {
        const prevItem = prevItems.find(p => p.id === item.id);
        return prevItem && item.quantity > prevItem.quantity;
      });

      if (newItems.length > 0) {
        setNotification({
          type: 'inventory',
          message: 'New item acquired!',
          itemName: newItems[0].name,
        });
      } else if (increasedItems.length > 0) {
        setNotification({
          type: 'inventory',
          message: 'Item added to inventory',
          itemName: increasedItems[0].name,
        });
      }
      prevInventoryRef.current = [...gameState.inventory];
    }
  }, [playerState?.energy, gameState?.inventory]);

  // Player messages hook
  const playerMessages = usePlayerMessages(
    currentStage as StageId | null,
    (playerState?.role ?? null),
    sessionId
  );

  // Detect stage changes to show message notification
  useEffect(() => {
    if (currentStage === null) return;

    // Skip first render
    if (prevStageForMessagesRef.current === null) {
      prevStageForMessagesRef.current = currentStage;
      return;
    }

    if (prevStageForMessagesRef.current !== currentStage) {
      prevStageForMessagesRef.current = currentStage;
      // Show notification if there are new unread messages for this stage
      if (playerMessages.newMessages.length > 0) {
        const stageInfo = STAGES[currentStage as StageId];
        setMessageNotification({
          count: playerMessages.newMessages.length,
          stageName: stageInfo ? `Stage ${currentStage}: ${stageInfo.name}` : `Stage ${currentStage}`,
        });
      }
    }
  }, [currentStage, playerMessages.newMessages]);

  const isComplete = session?.status === SessionStatus.COMPLETE;
  const isGameComplete = gameState?.gameStatus === 'completed' || gameState?.gameStatus === 'failed';
  const isGameActive = session?.status === SessionStatus.LIVE && gameState && playerState && !isGameComplete;

  // Capture player state when game completes (scores may become unavailable after session transitions)
  useEffect(() => {
    if (isGameComplete && playerState && !lastPlayerStateRef.current) {
      lastPlayerStateRef.current = playerState;
    }
  }, [isGameComplete, playerState]);

  // Loading state
  if (loading && !session) {
    return (
      <div className="h-screen flex flex-col bg-gradient-to-br from-[#E2E5EC] via-white to-[#CED4DE]">
        <Header sessionCode="..." status={SessionStatus.WAITING} />
        <main className="flex-1 flex items-center justify-center">
          <LoadingState />
        </main>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="h-screen flex flex-col bg-gradient-to-br from-[#E2E5EC] via-white to-[#CED4DE]">
        <Header sessionCode={session?.code || '...'} status={session?.status || SessionStatus.WAITING} />
        <main className="flex-1 flex items-center justify-center">
          <ErrorState message={error} onBack={() => navigate('/join')} />
        </main>
      </div>
    );
  }

  // Player not found state
  if (!session || !currentPlayer) {
    return (
      <div className="h-screen flex flex-col bg-gradient-to-br from-[#E2E5EC] via-white to-[#CED4DE]">
        <Header sessionCode="..." status={SessionStatus.WAITING} />
        <main className="flex-1 flex items-center justify-center">
          <PlayerNotFoundState onBack={() => navigate('/join')} />
        </main>
      </div>
    );
  }

  const roleConfig = getRoleConfig(currentPlayer.role || '');
  const playerIconUrl = getPlayerIcon(currentPlayer.role || '');

  return (
    <div className="relative h-screen overflow-hidden bg-gradient-to-br from-[#E2E5EC] via-white to-[#CED4DE]">
      <div className="pointer-events-none absolute -left-24 top-16 h-72 w-72 rounded-full bg-[#12356B]/10 blur-3xl" />
      <div className="pointer-events-none absolute -right-24 bottom-16 h-72 w-72 rounded-full bg-[#DD6832]/10 blur-3xl" />
      <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col">
        <Header
          sessionCode={session.code}
          status={session.status}
          tabs={
            <TabsList className="h-11 rounded-xl border border-[#CED4DE] bg-[#E2E5EC]/80 p-1 gap-2 sm:gap-3">
              <TabsTrigger
                value="game"
                className="h-9 px-3 rounded-lg bg-transparent text-slate-600 data-[state=active]:bg-white data-[state=active]:text-[#12356B] data-[state=active]:shadow-sm font-medium text-sm"
                disabled={!isGameActive}
              >
                <Gamepad2 className="size-4 mr-1.5" />
                <span className="hidden sm:inline">Game</span>
                {isGameActive && (
                  <span className="ml-1.5 size-2 rounded-full bg-[#367A25] animate-pulse" />
                )}
              </TabsTrigger>
              <TabsTrigger
                value="inventory"
                className="h-9 px-3 rounded-lg bg-transparent text-slate-600 data-[state=active]:bg-white data-[state=active]:text-[#12356B] data-[state=active]:shadow-sm font-medium text-sm"
                disabled={!isGameActive}
              >
                <Package className="size-4 mr-1.5" />
                <span className="hidden sm:inline">Inventory</span>
              </TabsTrigger>
              <TabsTrigger
                value="team"
                className="h-9 px-3 rounded-lg bg-transparent text-slate-600 data-[state=active]:bg-white data-[state=active]:text-[#12356B] data-[state=active]:shadow-sm font-medium text-sm"
              >
                <Users className="size-4 mr-1.5" />
                <span className="hidden sm:inline">Team</span>
              </TabsTrigger>
              <TabsTrigger
                value="documents"
                className="h-9 px-3 rounded-lg bg-transparent text-slate-600 data-[state=active]:bg-white data-[state=active]:text-[#12356B] data-[state=active]:shadow-sm font-medium text-sm"
              >
                <FileText className="size-4 mr-1.5" />
                <span className="hidden sm:inline">Documents</span>
              </TabsTrigger>
            </TabsList>
          }
          playerInfo={
            <div className="flex items-center gap-3">
              <div className={cn(
                'size-10 rounded-full flex items-center justify-center border border-[#CED4DE] bg-white',
                roleConfig.bgColor
              )}>
                {playerIconUrl ? (
                  <img src={playerIconUrl} alt={currentPlayer.role || ''} className="size-7 object-contain" />
                ) : (
                  <span className={cn('text-base font-medium', roleConfig.color)}>
                    {currentPlayer.displayName.charAt(0)}
                  </span>
                )}
              </div>
              <div className="text-base hidden sm:block">
                <p className="font-semibold leading-tight text-slate-900">{currentPlayer.displayName}</p>
                <p className={cn('text-sm', roleConfig.color)}>{currentPlayer.role || 'Unassigned'}</p>
              </div>
              {/* Bell icon for messages */}
              <button
                onClick={() => setIsMessagePanelOpen(true)}
                className="relative size-10 rounded-full flex items-center justify-center border border-[#CED4DE] bg-white hover:bg-[#E2E5EC] transition-colors"
                aria-label="Open transmissions"
              >
                <Bell className="size-5 text-[#12356B]" />
                {playerMessages.unreadCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 size-5 rounded-full bg-[#DD6832] text-white text-xs font-bold flex items-center justify-center animate-pulse">
                    {playerMessages.unreadCount > 9 ? '9+' : playerMessages.unreadCount}
                  </span>
                )}
              </button>
              {playerState && (() => {
                const energy = playerState.energy;
                const bgColor = energy > 75 ? 'text-emerald-200' : energy >= 40 ? 'text-yellow-200' : 'text-red-200';
                const ringColor = energy > 75 ? 'text-emerald-500' : energy >= 40 ? 'text-yellow-500' : 'text-red-500';
                const textColor = energy > 75 ? 'text-emerald-600' : energy >= 40 ? 'text-yellow-600' : 'text-red-600';

                return (
                  <div className="relative size-12 flex items-center justify-center">
                    {/* Background circle */}
                    <svg className="absolute inset-0 size-12 -rotate-90">
                      <circle
                        cx="24"
                        cy="24"
                        r="20"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="4"
                        className={bgColor}
                      />
                      <circle
                        cx="24"
                        cy="24"
                        r="20"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="4"
                        strokeLinecap="round"
                        className={ringColor}
                        strokeDasharray={`${(energy / 100) * 125.6} 125.6`}
                      />
                    </svg>
                    {/* Energy number */}
                    <span className={cn('text-sm font-bold tabular-nums', textColor)}>{energy}</span>
                  </div>
                );
              })()}
            </div>
          }
        />

        <main className="flex-1 flex flex-col min-h-0">
          {/* Tab Content */}
          <div className="flex-1 overflow-y-auto p-4 sm:p-6 scrollbar-thin relative z-10" style={{ maxHeight: 'calc(100vh - 120px)' }}>
            <TabsContent value="game" className={cn("m-0 h-full", activeTab !== 'game' && 'hidden')} forceMount>
              <div className="max-w-7xl mx-auto">
                {isGameComplete && gameState ? (
                  !emailSubmitted ? (
                    <EmailCollectionForm
                      playerName={currentPlayer.displayName}
                      teamName={gameState.teamName}
                      gameCode={session.code}
                      missionSuccess={gameState.gameStatus === 'completed'}
                      onSubmit={async (email) => {
                        if (sessionId && playerId) {
                          await sessionsService.savePlayerEmail(sessionId, playerId, email);
                        }
                        setEmailSubmitted(true);
                      }}
                      onSkip={() => setEmailSubmitted(true)}
                    />
                  ) : (
                    <EnhancedResultsScreen
                      gameState={gameState}
                      playerState={lastPlayerStateRef.current || playerState!}
                      sessionCode={session.code}
                      onJoinAnother={() => navigate('/join')}
                    />
                  )
                ) : isGameActive ? (
                  <GameView
                    gameState={gameState}
                    player={playerState}
                    onSubmitAnswer={handleSubmitAnswer}
                    answeredQuestions={answeredQuestions}
                    onMessagesAcknowledged={() => {
                      // Mark current stage messages as read
                      playerMessages.messages
                        .filter(m => m.stage === gameState.currentStage && !m.isRead)
                        .forEach(m => playerMessages.markRead(m.id));
                      playerMessages.clearNewMessages();
                    }}
                  />
                ) : (
                  <div className="flex flex-col items-center justify-center py-16 text-center rounded-2xl border border-[#CED4DE] bg-white shadow-sm">
                    <div className="size-12 rounded-full bg-[#E2E5EC] flex items-center justify-center mb-4">
                      <Gamepad2 className="size-6 text-[#12356B]" />
                    </div>
                    <h3 className="font-semibold text-lg mb-1 text-[#12356B]">Waiting for game to start...</h3>
                    <p className="text-sm text-slate-600">
                      The game master will start the session shortly.
                    </p>
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="team" className="m-0 h-full">
              <div className="max-w-3xl mx-auto">
                <TeamRoster
                  players={session.players.map(p => ({
                    id: p.playerId,
                    name: p.displayName,
                    role: p.role || '',
                  }))}
                  currentPlayerId={playerId || ''}
                />
              </div>
            </TabsContent>

            <TabsContent value="documents" className="m-0 h-full">
              <div className="max-w-3xl mx-auto">
                <MissionDocuments role={currentPlayer.role || ''} />
              </div>
            </TabsContent>

            <TabsContent value="inventory" className="m-0 h-full">
              <InventoryPage
                inventory={gameState?.inventory || []}
                teamEnergyPacks={gameState?.teamEnergyPacks || 0}
              />
            </TabsContent>
          </div>
        </main>
      </Tabs>

      {/* Session Ended by Teacher (mid-game) */}
      {isComplete && !isGameComplete && (
        <div className="fixed inset-0 bg-[#101822]/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-white border border-[#CED4DE] rounded-2xl p-8 text-center shadow-xl">
            <div className="size-20 rounded-full bg-[#E2E5EC] flex items-center justify-center mx-auto mb-6">
              <svg
                className="size-10 text-[#DD6832]"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <h2 className="text-2xl font-bold mb-3 text-[#12356B]">Session Ended</h2>
            <p className="text-lg text-slate-700 mb-2">
              The facilitator has ended this session.
            </p>
            <p className="text-sm text-slate-600 mb-8">
              If this was unexpected, please check with your facilitator.
            </p>
            <Button onClick={() => navigate('/join')} size="lg" className="w-full bg-[#367A25] hover:bg-[#2d6820] text-white">
              Join Another Session
            </Button>
          </div>
        </div>
      )}

      {/* Energy/Inventory Change Notification */}
      <GameNotification
        type={notification?.type || 'energy'}
        message={notification?.message || ''}
        change={notification?.change}
        itemName={notification?.itemName}
        isVisible={!!notification}
        onDismiss={() => setNotification(null)}
      />

      {/* Message Panel */}
      <MessagePanel
        isOpen={isMessagePanelOpen}
        onClose={() => setIsMessagePanelOpen(false)}
        messages={playerMessages.messages}
        unreadCount={playerMessages.unreadCount}
        onMarkAllRead={playerMessages.markAllRead}
        onMarkRead={playerMessages.markRead}
      />

      {/* Message Notification Toast - suppressed during active gameplay (briefing handles it) */}
      <MessageNotification
        count={messageNotification?.count || 0}
        stageName={messageNotification?.stageName || ''}
        isVisible={!!messageNotification && !isGameActive}
        onDismiss={() => {
          setMessageNotification(null);
          playerMessages.clearNewMessages();
        }}
        onView={() => setIsMessagePanelOpen(true)}
      />

    </div>
  );
}
